// src/components/SectionEditOverlay.js
import { Copy, Edit, MoveDown, MoveUp, Plus, Trash2 } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";

const SectionEditOverlay = ({
    section,
    pageId,
    sectionIndex,
    totalSections,
    onSectionEdit,
}) => {
    const { isAdmin } = useAuth();
    const navigate = useNavigate();
    const [isHovered, setIsHovered] = useState(false);
    const [showActions, setShowActions] = useState(false);

    // Don't show overlay if user is not admin
    if (!isAdmin) {
        return null;
    }

    // Navigate to edit this specific section
    const editSection = () => {
        if (pageId && section._id) {
            navigate(`/pitech/pages/${pageId}#section-${section._id}`);
        }
    };

    // Add section after this one
    const addSectionAfter = () => {
        if (pageId) {
            navigate(`/pitech/pages/${pageId}#add-section-after-${section._id}`);
        }
    };

    // Delete section (would need to implement API call)
    const deleteSection = () => {
        if (window.confirm("Are you sure you want to delete this section?")) {
            // This would need to be implemented with proper API call
            console.log("Delete section:", section._id);
            // You could call a prop function here to handle the deletion
            if (onSectionEdit) {
                onSectionEdit("delete", section._id);
            }
        }
    };

    // Move section up/down (would need to implement API call)
    const moveSection = (direction) => {
        console.log(`Move section ${direction}:`, section._id);
        // This would need to be implemented with proper API call
        if (onSectionEdit) {
            onSectionEdit("move", section._id, direction);
        }
    };

    // Duplicate section (would need to implement API call)
    const duplicateSection = () => {
        console.log("Duplicate section:", section._id);
        // This would need to be implemented with proper API call
        if (onSectionEdit) {
            onSectionEdit("duplicate", section._id);
        }
    };

    // Get section type display name
    const getSectionTypeName = (type) => {
        return type
            .replace(/([A-Z])/g, " $1")
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    };

    // Get section preview text
    const getSectionPreview = () => {
        const { content } = section;
        if (content?.title) {
            return (
                content.title.substring(0, 30) +
                (content.title.length > 30 ? "..." : "")
            );
        }
        if (content?.text) {
            return (
                content.text.substring(0, 30) + (content.text.length > 30 ? "..." : "")
            );
        }
        return "No content";
    };

    return (
        <div
            className="relative group"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => {
                setIsHovered(false);
                setShowActions(false);
            }}
        >
            {/* Overlay that appears on hover */}
            {isHovered && (
                <>
                    {/* Blue border overlay */}
                    <div className="absolute inset-0 border-2 border-blue-500 pointer-events-none z-40 rounded">
                        {/* Section label */}
                        <div className="absolute -top-8 left-0 bg-blue-500 text-white px-2 py-1 text-xs rounded-t flex items-center space-x-2">
                            <span className="font-medium">
                                {getSectionTypeName(section.type)}
                            </span>
                            <span className="text-blue-200">{getSectionPreview()}</span>
                        </div>

                        {/* Edit button (always visible on hover) */}
                        <button
                            onClick={editSection}
                            className="absolute -top-8 right-0 bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 text-xs rounded-t flex items-center transition-colors"
                        >
                            <Edit className="w-3 h-3 mr-1" />
                            Edit
                        </button>
                    </div>

                    {/* Action buttons panel */}
                    <div className="absolute top-2 right-2 z-50">
                        {!showActions ? (
                            <button
                                onClick={() => setShowActions(true)}
                                className="bg-white shadow-lg border rounded-full p-2 hover:bg-gray-50 transition-colors"
                                title="More actions"
                            >
                                <Edit className="w-4 h-4 text-gray-600" />
                            </button>
                        ) : (
                            <div className="bg-white shadow-lg border rounded-lg p-2 space-y-1">
                                {/* Edit button */}
                                <button
                                    onClick={editSection}
                                    className="w-full flex items-center px-3 py-2 text-sm hover:bg-gray-100 rounded"
                                    title="Edit section"
                                >
                                    <Edit className="w-4 h-4 mr-2 text-blue-600" />
                                    Edit
                                </button>

                                {/* Add section after */}
                                <button
                                    onClick={addSectionAfter}
                                    className="w-full flex items-center px-3 py-2 text-sm hover:bg-gray-100 rounded"
                                    title="Add section after"
                                >
                                    <Plus className="w-4 h-4 mr-2 text-green-600" />
                                    Add After
                                </button>

                                {/* Move up */}
                                {sectionIndex > 0 && (
                                    <button
                                        onClick={() => moveSection("up")}
                                        className="w-full flex items-center px-3 py-2 text-sm hover:bg-gray-100 rounded"
                                        title="Move up"
                                    >
                                        <MoveUp className="w-4 h-4 mr-2 text-purple-600" />
                                        Move Up
                                    </button>
                                )}

                                {/* Move down */}
                                {sectionIndex < totalSections - 1 && (
                                    <button
                                        onClick={() => moveSection("down")}
                                        className="w-full flex items-center px-3 py-2 text-sm hover:bg-gray-100 rounded"
                                        title="Move down"
                                    >
                                        <MoveDown className="w-4 h-4 mr-2 text-purple-600" />
                                        Move Down
                                    </button>
                                )}

                                {/* Duplicate */}
                                <button
                                    onClick={duplicateSection}
                                    className="w-full flex items-center px-3 py-2 text-sm hover:bg-gray-100 rounded"
                                    title="Duplicate section"
                                >
                                    <Copy className="w-4 h-4 mr-2 text-orange-600" />
                                    Duplicate
                                </button>

                                {/* Delete */}
                                <hr className="my-1" />
                                <button
                                    onClick={deleteSection}
                                    className="w-full flex items-center px-3 py-2 text-sm hover:bg-red-50 rounded text-red-600"
                                    title="Delete section"
                                >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                </button>

                                {/* Close button */}
                                <hr className="my-1" />
                                <button
                                    onClick={() => setShowActions(false)}
                                    className="w-full text-center px-3 py-1 text-xs text-gray-500 hover:bg-gray-100 rounded"
                                >
                                    Close
                                </button>
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default SectionEditOverlay;
